
import React, { useMemo, useState } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { COST_CATEGORIES, ADMIN_PASSWORD, API_BASE_URL } from '../constants';
import { Project, Company, MainCostCategory, PieChartSegment, ProjectCosts, UpdateObraData } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { InfoCardItem } from '../components/InfoCardItem';
import { PieChartDisplay } from '../components/PieChartDisplay';
import { useExpenses } from '../hooks/useExpenses';
import { useProjects } from '../hooks/useProjects';
import { useAuth } from '../hooks/useAuth';
import { useCompanies } from '../hooks/useCompanies';
import { EditObraModal } from '../components/EditObraModal';
import { DeleteObraModal } from '../components/DeleteObraModal';

// Icons for Info Cards
import { UserIcon } from '../components/icons/UserIcon';
import { IdentificationIcon } from '../components/icons/IdentificationIcon';
import { ClipboardDocumentListIcon } from '../components/icons/ClipboardDocumentListIcon';
import { MapPinIcon } from '../components/icons/MapPinIcon';
import { PhoneIcon } from '../components/icons/PhoneIcon';

// Icons for Actions Card
import { LockClosedIcon } from '../components/icons/LockClosedIcon';
import { PrinterIcon } from '../components/icons/PrinterIcon';
import { EditIcon } from '../components/icons/EditIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { ChevronRightIcon } from '../components/icons/ChevronRightIcon';

const formatCurrency = (value?: number): string => {
  if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const ObraDetailPage: React.FC = () => {
  const { obraId } = ReactRouterDom.useParams<{ obraId: string }>();
  const navigate = ReactRouterDom.useNavigate();
  const { allExpenses, deleteExpensesByProjectId } = useExpenses();
  const { projects, updateProject, deleteProject, toggleLockProject } = useProjects();
  const { companies } = useCompanies();
  const { user } = useAuth();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
  const company = useMemo(() => {
    if (project?.companyId) {
      return companies.find(c => c.id === project.companyId);
    }
    return undefined;
  }, [project, companies]);

  const projectCosts = useMemo(() => {
    const expensesForObra = allExpenses.filter(e => e.projectId === obraId);

    const initialCosts: ProjectCosts = {
      [MainCostCategory.CDO]: 0,
      [MainCostCategory.CIO]: 0,
      [MainCostCategory.DESPESAS_ADM]: 0,
      [MainCostCategory.TRIBUTOS]: 0,
      [MainCostCategory.TRIBUTOS_INSS]: 0,
      total: 0,
    };

    if (!expensesForObra.length) {
        return initialCosts;
    }

    return expensesForObra.reduce((acc, expense) => {
        const amount = Number(expense.amount) || 0;
        let categoryId: MainCostCategory | undefined;

        // Robustly find category ID, whether `mainCategory` is an ID or a full name string from the DB
        const categoryInfo = COST_CATEGORIES.find(c => c.id === expense.mainCategory?.toLowerCase() || c.name === expense.mainCategory);
        if (categoryInfo) {
            categoryId = categoryInfo.id;
        }

        // Only add to specific category if found, but always add to total
        if (categoryId && acc.hasOwnProperty(categoryId)) {
            acc[categoryId] = (acc[categoryId] || 0) + amount;
        }
        
        acc.total += amount;
        return acc;
    }, initialCosts);

  }, [allExpenses, obraId]);

  const pieChartData: PieChartSegment[] = useMemo(() => {
    if (!projectCosts || projectCosts.total === 0) return [];
    
    const colors: { [key in MainCostCategory]?: string } = {
      [MainCostCategory.CDO]: '#FF6384', // Example Red
      [MainCostCategory.CIO]: '#36A2EB', // Example Blue
      [MainCostCategory.TRIBUTOS_INSS]: '#FFCE56', // Example Yellow
      [MainCostCategory.DESPESAS_ADM]: '#4BC0C0', // Example Teal
      [MainCostCategory.TRIBUTOS]: '#9966FF', // Example Purple
    };

    return Object.entries(projectCosts)
      .filter(([key, value]) => key !== 'total' && value > 0)
      .map(([key, value]) => {
        const categoryKey = key as MainCostCategory;
        const categoryInfo = COST_CATEGORIES.find(c => c.id === categoryKey);
        return {
          name: categoryInfo?.name.split(" - ")[1] || categoryInfo?.name || 'Outros', // Simplify name for legend
          value: value,
          percentage: (value / projectCosts.total) * 100,
          color: colors[categoryKey] || '#CCCCCC', // Default color
        };
      });
  }, [projectCosts]);


  if (!project || !user) {
    return (
      <div className="text-center py-10">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">Obra não encontrada</h1>
        <Button onClick={() => navigate('/obras')} variant="secondary" className="mt-4">
          Voltar para Obras
        </Button>
      </div>
    );
  }

  const isLocked = project.isLocked;
  const isLockedByCurrentUser = isLocked && project.lockedBy === user.id;
  const canToggleLock = !isLocked || isLockedByCurrentUser;

  const handleSaveEdit = (data: UpdateObraData) => {
    if (!obraId) return;
    updateProject(obraId, data);
    setIsEditModalOpen(false);
  };

  const handleConfirmDelete = async (enteredPassword: string) => {
    setIsDeleting(true);
    setDeleteErrorMessage(null);

    try {
      let isPasswordValid = false;

      // 1. Senha mestra (compatibilidade e fallback imediato)
      if (enteredPassword === ADMIN_PASSWORD) {
        isPasswordValid = true;
      } else {
        // 2. Validação pela API no servidor (testa senha do usuário logado ou administradores/devs)
        try {
          const res = await fetch(`${API_BASE_URL}/verify-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              password: enteredPassword,
              userId: user?.id,
              email: user?.email,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            isPasswordValid = Boolean(data.valid);
          } else {
            // Em caso de offline ou retorno não-200, checar senha do usuário logado em memória
            if (user?.password && user.password === enteredPassword) {
              isPasswordValid = true;
            }
          }
        } catch (netErr) {
          // Falha de conexão/offline
          if (user?.password && user.password === enteredPassword) {
            isPasswordValid = true;
          }
        }
      }

      if (!isPasswordValid) {
        setDeleteErrorMessage('Senha incorreta. A exclusão foi cancelada.');
        setIsDeleting(false);
        return;
      }

      if (obraId) {
        await deleteProject(obraId);
        await deleteExpensesByProjectId(obraId);
        setIsDeleteModalOpen(false);
        alert('Obra e todos os seus lançamentos foram excluídos com sucesso.');
        navigate('/obras', { replace: true });
      }
    } catch (err: any) {
      console.error('Erro ao excluir obra:', err);
      setDeleteErrorMessage(err.message || 'Falha ao excluir a obra no servidor.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleCategoryClick = (categoryId: MainCostCategory) => {
    navigate(`/obras/${project.id}/custos/${encodeURIComponent(categoryId)}`);
  };

  const costDisplayOrder: MainCostCategory[] = [
    MainCostCategory.CDO,
    MainCostCategory.CIO,
    MainCostCategory.DESPESAS_ADM,
    MainCostCategory.TRIBUTOS,
    MainCostCategory.TRIBUTOS_INSS,
  ];

  return (
    <div className="text-gray-900 dark:text-dark-text-primary">
      {/* Header Bar */}
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/obras')}
          className="p-2 mr-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary"
          aria-label="Voltar para a lista de Obras"
        >
          <ArrowLeftIcon className="text-2xl" />
        </Button>
        <h1 className="text-2xl font-semibold">
          Obra &gt; <span className="text-brand-lime">{project.name}</span>
        </h1>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column */}
        <div className="flex-grow lg:w-2/3 space-y-6">
          {/* Project Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <InfoCardItem icon={<UserIcon className="text-xl text-brand-teal" />} label="Encarregado" value={project.personInCharge || 'Não informado'} />
              <InfoCardItem icon={<IdentificationIcon className="text-xl text-brand-teal" />} label="CNPJ" value={company?.cnpj || 'Não informado'} />
              <InfoCardItem icon={<ClipboardDocumentListIcon className="text-xl text-brand-teal" />} label="Descrição do Serviço" value={project.serviceDescription || 'Não informado'} isBlock />
            </Card>
            <Card>
              <InfoCardItem icon={<MapPinIcon className="text-xl text-brand-teal" />} label="Endereço" value={company ? `${company.address.street}, ${company.address.number || 'S/N'} - ${company.address.neighborhood}, ${company.address.city}/${company.address.state}` : 'Não informado'} isBlock />
              <InfoCardItem icon={<PhoneIcon className="text-xl text-brand-teal" />} label="Telefone" value={company?.phone || 'Não informado'} />
            </Card>
          </div>

          {/* Cost Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <Card title="Custo total por Categoria" className="md:col-span-1">
              {pieChartData.length > 0 ? (
                <div className="flex items-center justify-center h-full">
                  <PieChartDisplay data={pieChartData} layout="horizontal" size={180} responsive={true} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 dark:text-dark-text-secondary text-center py-8">Nenhum custo lançado para exibir gráfico.</p>
                </div>
              )}
            </Card>

            <Card title="Custos Individuais" className="md:col-span-1">
              <div className="space-y-2">
                {costDisplayOrder.map(catId => {
                  const categoryInfo = COST_CATEGORIES.find(c => c.id === catId);
                  const costValue = projectCosts[catId];
                  return (
                    <button
                      key={catId}
                      onClick={() => handleCategoryClick(catId)}
                      className="w-full flex justify-between items-center p-3 bg-gray-100 dark:bg-dark-secondary rounded-md hover:bg-gray-200 dark:hover:bg-dark-tertiary transition-colors duration-150 text-left"
                      aria-label={`Ver detalhes de ${categoryInfo?.name}`}
                    >
                      <span className="text-sm text-gray-600 dark:text-dark-text-secondary flex-1 truncate pr-2">{categoryInfo?.name || catId}</span>
                      <div className="flex items-center">
                        <span className={`text-md font-semibold ${costValue > 0 ? 'text-gray-900 dark:text-dark-text-primary' : 'text-gray-500'} mr-2`}>{formatCurrency(costValue)}</span>
                        <ChevronRightIcon className="text-lg text-gray-400"/>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-dark-tertiary flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900 dark:text-dark-text-primary">CUSTO TOTAL</span>
                <span className="text-xl font-bold text-brand-lime">{formatCurrency(projectCosts.total)}</span>
              </div>
            </Card>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:w-1/3">
          <Card title="Ações">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant={isLocked ? "warning" : "secondary"} 
                className="w-full flex-col h-20" 
                onClick={() => toggleLockProject(project.id, user.id)}
                disabled={!canToggleLock}
                title={!canToggleLock ? `Bloqueado por outro usuário` : (isLocked ? 'Desbloquear obra' : 'Bloquear obra para edições')}
              >
                <LockClosedIcon className="text-2xl mb-1" />
                <span>{isLocked ? "Desbloquear" : "Bloquear"}</span>
              </Button>
              <Button variant="secondary" className="w-full flex-col h-20" onClick={() => navigate(`/obras/${project.id}/relatorio`)}>
                <PrinterIcon className="text-2xl mb-1" />
                <span>Imprimir</span>
              </Button>
              <Button variant="secondary" className="w-full flex-col h-20" onClick={() => setIsEditModalOpen(true)} disabled={isLocked}>
                <EditIcon className="text-2xl mb-1" />
                <span>Editar</span>
              </Button>
              <Button 
                variant="danger" 
                className="w-full flex-col h-20" 
                onClick={() => {
                  setDeleteErrorMessage(null);
                  setIsDeleteModalOpen(true);
                }} 
                disabled={isLocked}
              >
                <TrashIcon className="text-2xl mb-1" />
                <span>Excluir</span>
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {isEditModalOpen && project && (
        <EditObraModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleSaveEdit}
          project={project}
        />
      )}

      {isDeleteModalOpen && project && (
        <DeleteObraModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            if (!isDeleting) {
              setIsDeleteModalOpen(false);
              setDeleteErrorMessage(null);
            }
          }}
          onConfirm={handleConfirmDelete}
          obraName={project.name}
          isLoading={isDeleting}
          errorMessage={deleteErrorMessage}
        />
      )}
    </div>
  );
};